<template>
    <Teleport to="body">
        <div v-show="displayModal"
             ref="theModal"
             class="modal fade show"
             :id="getModalId()"
             tabindex="-1"
             :aria-hidden="isAriaHidden"
             v-on:keyup.esc="closeModal()"
             :style="['display: block;', customStyles]">
            <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                 :class="getSizeClass">
                <div class="modal-content rounded-4 p-4">
                    <div class="modal-header bg-white pb-0" :class="headerClass">
                        <span v-show="showIcon()" class="float-start icon">
                            <template v-if="showCustomIconOverride">
                                <img :src="customIconOverride" style="width: 1.4rem;" />
                            </template>
                            <template v-else>
                                <i class="bi" :class="icon"></i>
                            </template>
                        </span>
                        <h3 class="modal-title">{{ title }}</h3>
                        <button v-if="showClose"
                                ref="closeButton"
                                type="button"
                                class="btn-close d-flex justify-content-center align-items-center"
                                v-on:click="closeModal()"
                                :aria-label="closeButtonAriaLabel">
                            <i class="bi bi-x-lg btn-close-x"></i>
                        </button>
                    </div>
                    <div class="modal-body py-0">
                        <slot></slot>
                    </div>
                </div>
            </div>
        </div>
        <div v-show="displayModal"
             class="modal-backdrop fade show"
             id="backdrop"
             style="display: block;"></div>
    </Teleport>
</template>

<script setup lang="ts">
    import { getUniqueNumber } from "@/guiide/ts/utilities";
    import { ref, computed, nextTick, defineEmits } from "vue";

    const props = defineProps({
        idKey: { type: String, required: true },
        title: { type: String, required: true },
        icon: { type: String, required: false, default: "" },
        isWide: { type: Boolean, required: false, default: false },
        showClose: { type: Boolean, default: true },
        returnFocusTo: { type: HTMLElement, required: false },
        checkForCustomIcon: { type: Boolean, default: false, required: false },
        customIconOverride: { type: String, default: "", required: false },
        closeButtonAriaLabel: { type: String, default: "Close", required: false },
        customStyles: { type: String, default: "", required: false },
        hideHeader: { type: Boolean, required: false, default: false }
    });

    defineExpose({ showModal, closeModal });

    const emit = defineEmits(['closed']);

    const theModal = ref(null);
    const closeButton = ref(null);
    const displayModal = ref(false);
    const isAriaHidden = ref(true);

    const getSizeClass = computed<string>(() => {
        return props.isWide ? "modal-lg" : "";
    });

    const showCustomIconOverride = computed<Boolean>(() => props.customIconOverride.length > 0);

    const headerClass = computed<String>(() => props.hideHeader ? "visually-hidden" : "");

    function showIcon() {
        return props.icon.length > 0;
    }

    async function showModal(initializer: () => void) {
        isAriaHidden.value = false;
        await nextTick();

        // Keep focus within modal while showing
        document.getElementById("app")?.setAttribute("inert", "");

        lockScroll();

        displayModal.value = true;

        if (initializer) {
            nextTick(initializer);
        } else {
            requestAnimationFrame(() => {
                closeButton.value?.focus();
            });
        }
    }

    function closeModal() {
        isAriaHidden.value = true;

        if (props.returnFocusTo) {
            requestAnimationFrame(() => {
                props.returnFocusTo?.focus();
            });
        }

        // Remove modal focus
        document.getElementById("app")?.removeAttribute("inert");

        unlockScroll();

        displayModal.value = false;

        emit('closed');
    }

    function getModalId(): string {
        const uniqueIdGuarantee = getUniqueNumber();
        return `${props.idKey}${uniqueIdGuarantee}Modal`;
    }

    function lockScroll() {
        document.body.dataset.scrollY = window.scrollY;

        document.body.style.overflowX = "hidden";
        document.body.style.overflowY = "hidden";

        document.body.style.position = "relative";
    }

    function unlockScroll() {
        const y = parseInt(document.body.dataset.scrollY, 10);

        document.body.style.overflowX = "";
        document.body.style.overflowY = "";
        document.body.style.position = "";

        requestAnimationFrame(() => {
            window.scrollTo(0, y);
        });
    }

</script>

<style scoped>
    .modal-dialog-scrollable .modal-content {
        overflow: visible;
    }

    .btn-close {
        background-color: #fff;
        border-radius: 50px;
        padding: 19px !important;
        position: absolute;
        top: -20px;
        left: 50%;
        margin-left: -30px !important;
        opacity: 1;
        transition: transform .2s;
    }

        .btn-close:hover {
            transform: scale(1.1);
            opacity: 1;
        }

    .btn-close-x {
        font-size: 1.6rem;
    }

    .icon {
        margin-right: 10px;
        display: none;
    }

    .modal-header {
        border-bottom: none !important;
    }

    .modal-backdrop {
        position: fixed !important;
        inset: 0;
        width: 100vw;
        height: 100vh;
    }
</style>
